import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AuthShell, FeatIcon, SvgArrow, SvgGoogle,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";
import { getName } from "../../lib/anon-reflection.js";

/**
 * /sign-up — name + email, opt-in weekly reflection.
 *
 * Direct port of `signup.html`. Sends the user to /verify?new=1
 * which renders the 3-step stepper rather than the 2-step
 * returning-user variant.
 */
export const SignUp = () => {
  const navigate = useNavigate();
  const { requestSignUp } = useAuth();
  // Pre-fill from the device name captured during an anon reflection.
  const [name, setName] = useState(() => getName() ?? "");
  const [email, setEmail] = useState("");
  const [weekly, setWeekly] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError(null);
    const result = await requestSignUp(email, name);
    setSubmitting(false);
    if (!result.ok) { setError(result.message); return; }
    // While the worker has no email provider, the code comes back in
    // `devOtp` so the next page can pre-fill it. Self-disables once
    // email is wired.
    sessionStorage.setItem("wo.auth.email", email.trim());
    sessionStorage.setItem("wo.auth.name", name.trim());
    sessionStorage.setItem("wo.auth.weekly", String(weekly));
    if (result.devOtp) sessionStorage.setItem("wo.auth.devOtp", result.devOtp);
    else sessionStorage.removeItem("wo.auth.devOtp");
    navigate("/verify?new=1");
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "Begin gently",
        quote: <>Make a small, private space — and return to it on the days you forget how to breathe.</>,
        cite: "— On starting",
        features: [
          { icon: FeatIcon.spark, label: "A daily 3-minute reset" },
          { icon: FeatIcon.book, label: "A journal that reflects back" },
          { icon: FeatIcon.chat, label: "A wise companion to talk to" },
        ],
      }}
      topRight={<Link to="/sign-in" className="btn-ghost">Sign in</Link>}
      footer={
        <>
          By continuing you accept our <Link to="/terms">Terms</Link> &amp; <Link to="/privacy">Privacy</Link>.<br />
          Already have an account? <Link to="/sign-in">Sign in →</Link>
        </>
      }
    >
      <div className="stepper">
        <div className="stepper-dot on" />
        <div className="stepper-dot" />
        <div className="stepper-dot" />
      </div>

      <div className="eyebrow mustard">Create your private space</div>
      <h1 className="wo-title" style={{ marginTop: 6 }}>
        A quieter mind,<br />day by day.
      </h1>
      <p className="wo-sub">
        Start with your email — we&apos;ll send a code, then ask one or two gentle questions. Takes about a minute.
      </p>

      <form onSubmit={onSubmit}>
        <div className="field">
          <label className="field-label" htmlFor="su-name">Your first name</label>
          <input
            id="su-name" className="field-input" type="text"
            placeholder="What should we call you?" autoComplete="given-name"
            value={name} onChange={(e) => setName(e.target.value)} required
          />
          <div className="field-help">Only used to address you. Never shared.</div>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="su-email">Email</label>
          <input
            id="su-email" className="field-input" type="email"
            placeholder="you@email.com" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)} required
          />
          <div className="field-help">We&apos;ll send a 6-digit code to confirm.</div>
        </div>

        <label style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          fontSize: 12.5, color: "rgb(var(--wo-ink-soft))",
          lineHeight: 1.5, margin: "14px 0 20px",
        }}>
          <input
            type="checkbox"
            checked={weekly}
            onChange={(e) => setWeekly(e.target.checked)}
            style={{ marginTop: 3, accentColor: "rgb(var(--wo-teal))", width: 16, height: 16 }}
          />
          <span>
            I&apos;d like a gentle weekly reflection email.{" "}
            <span style={{ color: "rgb(var(--wo-muted))" }}>(You can turn this off anytime.)</span>
          </span>
        </label>

        {error && (
          <div className="field-help" style={{ color: "rgb(var(--wo-danger))", marginBottom: 10 }}>{error}</div>
        )}
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? "Sending…" : "Send me a code"}
          {SvgArrow}
        </button>
      </form>

      <div className="or">or</div>

      <button type="button" className="btn-secondary">
        {SvgGoogle}
        Continue with Google
      </button>

      <p className="reflect">&ldquo;Beginnings don&apos;t need to be big.&rdquo;</p>
    </AuthShell>
  );
};
